import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { subscribeQueueUpdate } from '@/lib/queueSignal';
import { Music, Users, Clock, Crown } from "lucide-react";

interface QueueRequest {
  id: string;
  song_id: string;
  singer_name: string;
  status: string;
  queue_position: number;
  created_at: string;
  custom_song_title?: string;
  custom_song_artist?: string;
  songs?: {
    title: string;
    artist: string;
  };
}

interface Venue {
  id: string;
  name: string;
  welcome_message: string;
  primary_color: string;
  secondary_color: string;
}

const QueueProjection = () => {
  const { venue_slug } = useParams();
  const [queue, setQueue] = useState<QueueRequest[]>([]);
  const [venue, setVenue] = useState<Venue | null>(null);
  const [currentSong, setCurrentSong] = useState<QueueRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const venueRef = useRef<Venue | null>(null);

  useEffect(() => {
    venueRef.current = venue;
  }, [venue]);

  useEffect(() => {
    if (venue_slug) {
      loadVenueAndQueue();
      
      // Set up real-time subscription
      const subscription = supabase
        .channel('queue_projection')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'requests'
          },
          () => {
            loadQueue();
          }
        )
        .subscribe();

      // Subscribe to centralized queue signals (handles both storage + custom events)
      const unsubscribe = subscribeQueueUpdate(() => {
        loadQueue(venueRef.current?.id);
      });

      return () => {
        subscription.unsubscribe();
        unsubscribe();
      };
    }
  }, [venue_slug]);

  const loadVenueAndQueue = async () => {
    try {
      // Load venue
      const { data: venueData, error: venueError } = await supabase
        .from('venues')
        .select('id, name, welcome_message, primary_color, secondary_color')
        .eq('slug', venue_slug)
        .single();

      if (venueError) throw venueError;
      setVenue(venueData);

      // Load queue
      await loadQueue(venueData.id);
    } catch (error) {
      console.error('Error loading venue and queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQueue = async (venueId?: string) => {
    try {
      const targetVenueId = venueId || venue?.id;
      if (!targetVenueId) return;

      const { data, error } = await (supabase.from('requests') as any)
        .select('id, song_id, singer_name, status, queue_position, created_at, custom_song_title, custom_song_artist, songs (title, artist)')
        .eq('venue_id', targetVenueId)
        .in('status', ['queued', 'playing'])
        .eq('approval_status', 'approved')
        .order('queue_position');

      if (error) throw error;

      const queueData = (data || []) as QueueRequest[];
      setQueue(queueData);
      
      // Set current song (first in queue or playing)
      const playing = queueData.find(req => req.status === 'playing');
      const nextInQueue = queueData.find(req => req.status === 'queued');
      setCurrentSong(playing || nextInQueue || null);
    } catch (error) {
      console.error('Error loading queue:', error);
    }
  };

  const getSongTitle = (request: QueueRequest) => {
    return request.custom_song_title || request.songs?.title || 'Unknown Song';
  };

  const getSongArtist = (request: QueueRequest) => {
    return request.custom_song_artist || request.songs?.artist || 'Unknown Artist';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-violet-500 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <Music className="h-8 w-8 text-white" />
          </div>
          <p className="text-white text-xl">Loading Queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/20 p-6">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent">
                {venue?.name || 'Karaoke Queue'}
              </h1>
              <p className="text-base sm:text-xl text-white/80 mt-2">
                {venue?.welcome_message || 'Live Queue Display'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-pink-400">
                {queue.length}
              </div>
              <div className="text-sm text-white/60">
                Songs in Queue
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Song */}
      {currentSong && (
        <div className="bg-gradient-to-r from-pink-500/20 to-violet-500/20 border-y border-pink-500/30 p-8">
          <div className="container mx-auto">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Crown className="h-8 w-8 text-yellow-400" />
                <h2 className="text-2xl font-semibold text-yellow-400">NOW PERFORMING</h2>
                <Crown className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-5xl font-bold text-white">
                  {getSongTitle(currentSong)}
                </h3>
                <p className="text-3xl text-pink-300">
                  by {getSongArtist(currentSong)}
                </p>
                <p className="text-2xl text-white/80">
                  Performed by: <span className="font-semibold text-violet-300">{currentSong.singer_name}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Queue List */}
      <div className="container mx-auto p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-6">
            <Users className="h-6 w-6 text-violet-400" />
            <h2 className="text-2xl font-semibold text-white">Up Next</h2>
          </div>

          {queue.filter(req => req.id !== currentSong?.id).length === 0 ? (
            <div className="text-center py-12">
              <Music className="h-16 w-16 text-white/30 mx-auto mb-4" />
              <p className="text-2xl text-white/60">Queue is empty</p>
              <p className="text-lg text-white/40">Waiting for song requests...</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {queue
                .filter(req => req.id !== currentSong?.id)
                .slice(0, 20)
                .map((request, index) => (
                  <div
                    key={request.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-violet-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-2xl font-semibold text-white">
                          {getSongTitle(request)}
                        </h4>
                        <p className="text-lg text-pink-300">
                          by {getSongArtist(request)}
                        </p>
                        <p className="text-sm text-white/70">
                          Requested by: <span className="font-medium text-violet-300">{request.singer_name}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-white/60">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">~{(index + 1) * 4}m</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 mt-8 py-4">
        <div className="container mx-auto text-center">
          <p className="text-white/40 text-sm">Live Queue Updates</p>
        </div>
      </div>
    </div>
  );
};

export default QueueProjection;
